import { element } from '../../global/template-literals';
import { UIElement } from '../ui-element';
import { UIHint } from '../hint/ui-hint';
import { Labels } from '../../global/labels';
import { FormValidator } from '../../global/form-validator';
import { Digest } from '../../global/digest';
import { dispatchNativeEvent } from '../../global/events';
import { setNativeInputValue } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type {
    AttributeValue,
    ObservedAttributes,
    ElementConfig,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-textcounter.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UITextCounter
 * @element ui-textcounter
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-textcounter</code> element.
 * Counts and display character remaining for input and textarea elements.
 * @property {number} [limit=0] {@attr limit} Text counter limit.
 * @property {string} [label] {@attr label} Label for text counter.
 * @property {boolean} [overflow] {@attr overflow} Allows to charters counter hint to be negative.
 * @property {boolean} [validate] {@attr validate} Forces to validate textarea if character
 * overflow and character count is less than limit value.
 * @property {boolean} [nolabel] {@attr nolabel} Hides the label (characters remaining).
 * @property {string} [labelError] {@attr label-error} Custom error label when length is more
 * than limit.
 * @property {string} [controlId] {@attr control-id} Control (textarea element) id. Deprecated! Prefer setting id on the native textarea. Priority: native textarea id > control-id > uid.
 * @property {HTMLTextAreaElement} textarea Shortcut to a textarea element.
 * @property {UIHint} hint Shortcut to a hint element.
 * @property {HTMLSpanElement} characters Shortcut to a character container element.
 * @slot
 * @example
 *  <ui-field label="Message">
 *    <ui-textcounter limit="2000">
 *      <textarea id="test-render-id" name="message"></textarea>
 *    </ui-textcounter>
 *  </ui-field>
 */
@Component({ tag: 'ui-textcounter', styles })
export class UITextCounter extends UIElement {
    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['control-id'];
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-textcounter', {
            remaining: 'characters',
            invalid: 'Please use %d characters or fewer.',
        });
    }

    @Attribute(Number) limit: number = 0;
    @Attribute(String) label?: string;
    @Attribute(Boolean) overflow?: boolean;
    @Attribute(Boolean) validate?: boolean;
    @Attribute(Boolean) nolabel?: boolean;
    @Attribute(String) labelError?: string;
    @Attribute(String) controlId?: string;

    @Child('textarea') textarea!: HTMLTextAreaElement;
    @Child('ui-hint') hint!: UIHint;
    @Child('.ui-textcounter__characters') characters?: HTMLSpanElement;

    /** @private */
    private _descriptionId?: string;
    /** @private */
    private _id?: string;
    /** @private */
    private updateHandler?: EventListener;

    /**
     * @type {string}
     * @private
     */
    get descriptionId(): string {
        return (this._descriptionId = this._descriptionId || Digest.randomId());
    }

    /**
     * Stable generated id when neither textarea id nor control-id is set.
     * @private
     */
    get uid(): string {
        return (this._id = this._id || Digest.randomId());
    }

    /**
     * Returns the control id for ui-field (label[for], aria-describedby).
     * Priority: native textarea id > control-id > uid.
     * @returns {string}
     */
    getControlIdForField(): string {
        const controlIdAttr =
            this.getAttribute('control-id') || this.controlId || null;
        const textarea: NullOr<HTMLTextAreaElement> =
            this.textarea ||
            this.querySelector<HTMLTextAreaElement>('textarea') ||
            null;
        if (textarea) {
            const textareaId = textarea.getAttribute('id')?.trim();
            if (textareaId) {
                return textareaId;
            }
            if (controlIdAttr) {
                if (textarea.id !== controlIdAttr) {
                    textarea.id = controlIdAttr;
                    textarea.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = textarea.id || this.uid;
            if (textarea.id !== effectiveId) {
                textarea.id = effectiveId;
                textarea.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        return controlIdAttr || this.uid;
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'control-id':
                if (!this.textarea) {
                    break;
                }
                const currentId = this.textarea.getAttribute('id')?.trim();
                if (newValue) {
                    if (!currentId || currentId === oldValue) {
                        this.textarea.id = newValue;
                        this.textarea.setAttribute('id', newValue);
                    }
                } else if (currentId === oldValue) {
                    this.textarea.removeAttribute('id');
                }
                break;
        }
    }

    /**
     * @type {number}
     * @readonly
     */
    get charCount(): number {
        return this.textarea.value.length;
    }

    /**
     * @type {number}
     * @readonly
     */
    get remainingChars(): number {
        return this.limit - this.charCount;
    }

    /**
     * Sets character remaining text to an element.
     */
    setRemainingText(): void {
        if (this.characters) {
            this.characters.textContent = this.formatCounter();
        }
    }

    /**
     * Rebuilds extra hint of the textarea.
     */
    rebuildHint(): void {
        this.hint.rebuild(element`
      <ui-hint id="${this.descriptionId}">
        <span class="ui-textcounter__characters" aria-live="polite">
          ${this.formatCounter()}
        </span>
      </ui-hint>
    `);
    }

    /**
     * @private
     * @returns {string}
     */
    private formatCounter(): string {
        return `${this.charCount}/${this.limit} ${this.label || UITextCounter.labels.remaining}`;
    }

    /**
     * Updates counter value, cuts the textarea value if the character limit exceeded.
     */
    updateCounter(): void {
        if (!this.limit) {
            return;
        }

        if (this.hasOverflow() && !this.overflow) {
            const cutText = this.textarea.value.substring(0, this.limit);
            setNativeInputValue(this.textarea, cutText);
        }

        if (this.overflow && this.validate) {
            this.validateLength();
        }

        if (!this.nolabel) {
            this.setRemainingText();
        }
    }

    /**
     * Validates and set :invalid state to textarea if the characters are more than limit.
     */
    validateLength(): void {
        const validityMsg = (
            this.hasAttribute('label-error')
                ? this.labelError || ' '
                : String(UITextCounter.labels.invalid)
        ).replace('%d', String(this.limit));
        this.textarea.setCustomValidity(this.hasOverflow() ? validityMsg : '');
        FormValidator.validateField(this.textarea);
    }

    /**
     * Checks if a text is longer than the limit.
     * @returns {boolean}
     */
    hasOverflow(): boolean {
        return this.charCount > this.limit;
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLTextAreaElement {
        return this.textarea;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: string): void {
        setNativeInputValue(this.textarea, newValue);
        dispatchNativeEvent(this.textarea, 'input', true);
        this.updateCounter();
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.textarea.disabled = newState;
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (!this.limit) {
            return;
        }

        let textareaId = '';
        if (this.textarea) {
            const userProvidedId =
                this.textarea.getAttribute('id')?.trim() ||
                this.textarea.id ||
                '';
            if (!!userProvidedId) {
                textareaId = userProvidedId;
            } else {
                textareaId =
                    this.getAttribute('control-id') ||
                    this.controlId ||
                    this.uid;
                this.textarea.id = textareaId;
                this.textarea.setAttribute('id', textareaId);
            }
        }

        const children: Array<ElementConfig> = [
            {
                tagName: 'textarea',
                element: this.textarea,
                attributes: {
                    id: textareaId,
                    ...(!this.nolabel
                        ? { 'aria-describedby': this.descriptionId }
                        : {}),
                },
            },
        ];
        if (!this.nolabel) {
            children.push({ tagName: 'ui-hint' });
        }
        this.insertElements(children);
        this.updateCounter();
        if (!this.nolabel) {
            this.rebuildHint();
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        if (!this.limit) {
            return;
        }
        this.updateHandler = this.updateCounter.bind(this);
        this.textarea.addEventListener('input', this.updateHandler);

        // Init validation on start if required.
        if (this.hasOverflow() && this.overflow && this.validate) {
            this.validateLength();
        }
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.textarea.removeEventListener('input', this.updateHandler!);
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.textarea.addEventListener('input', this.updateHandler!);
    }
}
